import { create } from 'zustand';
import { CampAccommodationTable, CampExcelRow } from '../types/camp';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  deleteDoc,
  updateDoc,
  writeBatch,
  query,
  where,
} from 'firebase/firestore';
import { firestore } from '../lib/firebase/config';
import * as XLSX from 'xlsx';
import { normalizePassportNumber } from '../utils/passportUtils';
import { calculateAge } from '../utils/dateUtils';
import { useBansStore } from './bansStore';
import { useSuspensionStore } from './suspensionStore';
import { useLanguage } from '../contexts/LanguageContext'; // Import useLanguage

type CampAccommodationState = {
  tables: Record<string, CampAccommodationTable>;
  loading: boolean;
  error: string | null;
  fetchTables: () => Promise<void>;
  createTable: (
    campId: string,
    campName: string,
    tableName: string,
    file: File
  ) => Promise<void>;
  deleteTable: (tableId: string) => Promise<void>;
  assignPilgrim: (
    tableId: string,
    tentNumber: number,
    hallNumber: number,
    bedIndex: number,
    passportNumber: string
  ) => Promise<void>;
  removePilgrim: (
    tableId: string,
    tentNumber: number,
    hallNumber: number,
    bedIndex: number
  ) => Promise<void>;
};

export const useCampAccommodationStore = create<CampAccommodationState>(
  (set, get) => ({
    tables: {},
    loading: false,
    error: null,

    fetchTables: async () => {
      set({ loading: true, error: null });
      try {
        const querySnapshot = await getDocs(
          collection(firestore, 'CampAccommodationTables')
        );
        const tables: Record<string, CampAccommodationTable> = {};

        querySnapshot.forEach((doc) => {
          tables[doc.id] = {
            ...doc.data(),
            id: doc.id,
          } as CampAccommodationTable;
        });

        set({ tables, loading: false });
      } catch (error: any) {
        console.error('Error fetching tables:', error);
        set({ error: error.message, loading: false });
      }
    },

    createTable: async (campId, campName, tableName, file) => {
      set({ loading: true, error: null });
      try {
        // Check for service suspension
        const isSuspended = await useSuspensionStore
          .getState()
          .checkServiceSuspension('camp_accommodation');

        if (isSuspended) {
          throw new Error(
            useLanguage.getState().translations?.campAccommodation?.errors
              ?.serviceSuspended ||
              'Camp accommodation service is currently suspended'
          );
        }

        // Get current user info
        const userId = localStorage.getItem('userId');
        if (!userId)
          throw new Error(
            useLanguage.getState().translations?.campAccommodation?.errors
              ?.notAuthenticated || 'User not authenticated'
          );

        // Check for user ban
        const isUserBanned = await useBansStore
          .getState()
          .checkUserBans(userId, 'camp');

        if (isUserBanned) {
          throw new Error(
            useLanguage.getState().translations?.campAccommodation?.errors
              ?.userBanned ||
              'You are banned from using the camp accommodation service'
          );
        }

        // Parse Excel file
        const data = await parseExcelFile(file);
        const tents = processExcelData(data);

        // Create table object
        const tableRef = doc(collection(firestore, 'CampAccommodationTables'));
        const table: CampAccommodationTable = {
          id: tableRef.id,
          campId,
          campName,
          tableName,
          tents,
          createdAt: Date.now(),
          updatedAt: Date.now(),
          createdBy: userId,
          totalCapacity: calculateTotalCapacity(tents),
          occupiedCapacity: 0,
        };

        await setDoc(tableRef, table);

        set((state) => ({
          tables: {
            ...state.tables,
            [table.id]: table,
          },
          loading: false,
        }));
      } catch (error: any) {
        console.error('Error creating table:', error);
        set({ error: error.message, loading: false });
        throw error;
      }
    },

    assignPilgrim: async (
      tableId,
      tentNumber,
      hallNumber,
      bedIndex,
      passportNumber
    ) => {
      set({ loading: true, error: null });
      try {
        // Check for service suspension
        const isSuspended = await useSuspensionStore
          .getState()
          .checkServiceSuspension('camp_accommodation');

        if (isSuspended) {
          throw new Error(
            useLanguage.getState().translations?.campAccommodation?.errors
              ?.serviceSuspended ||
              'Camp accommodation service is currently suspended'
          );
        }

        // Get current user info
        const currentUserId = localStorage.getItem('userId');
        const userRole = localStorage.getItem('userRole');

        if (!currentUserId || !userRole) {
          throw new Error(
            useLanguage.getState().translations?.campAccommodation?.errors
              ?.notAuthorized || 'You are not authorized to perform this action'
          );
        }

        // Check for user ban
        const isUserBanned = await useBansStore
          .getState()
          .checkUserBans(currentUserId, 'camp');

        if (isUserBanned) {
          throw new Error(
            useLanguage.getState().translations?.campAccommodation?.errors
              ?.userBanned ||
              'You are banned from using the camp accommodation service'
          );
        }

        // Search for pilgrim
        const normalizedPassport = normalizePassportNumber(passportNumber);
        const [stringResults, numberResults] = await Promise.all([
          getDocs(
            query(
              collection(firestore, 'Pilgrims'),
              where('Passport_Number', '==', normalizedPassport)
            )
          ),
          getDocs(
            query(
              collection(firestore, 'Pilgrims'),
              where('Passport_Number', '==', parseInt(normalizedPassport))
            )
          ),
        ]);

        const results = [...stringResults.docs, ...numberResults.docs];
        if (results.length === 0) {
          throw new Error(
            useLanguage.getState().translations?.campAccommodation?.errors
              ?.pilgrimNotFound || 'No pilgrim found with this passport number'
          );
        }

        const pilgrimDoc = results[0];
        const pilgrimData = pilgrimDoc.data();

        // Get guide info
        const linksQuery = query(
          collection(firestore, 'PilgrimGuideLinks'),
          where('pilgrimId', '==', pilgrimDoc.id),
          where('status', '==', 'approved')
        );
        const linksSnapshot = await getDocs(linksQuery);

        if (linksSnapshot.empty) {
          throw new Error(
            useLanguage.getState().translations?.campAccommodation?.errors
              ?.noGuideLinked || 'Pilgrim is not linked to any guide'
          );
        }

        const linkData = linksSnapshot.docs[0].data();
        const guideId = linkData.guideId;
        const organizerId = linkData.organizerId;

        // Check permissions
        if (userRole === 'guide' && guideId !== currentUserId) {
          throw new Error(
            useLanguage.getState().translations?.campAccommodation?.errors
              ?.noPermission ||
              'You do not have permission to assign this pilgrim'
          );
        }

        if (userRole === 'organizer' && organizerId !== currentUserId) {
          throw new Error(
            useLanguage.getState().translations?.campAccommodation?.errors
              ?.noPermission ||
              'You do not have permission to assign this pilgrim'
          );
        }

        // Get guide and organizer information
        const [guideDoc, organizerDoc] = await Promise.all([
          getDoc(doc(firestore, 'Guides', guideId)),
          getDoc(doc(firestore, 'Organizers', organizerId)),
        ]);

        const guideName = guideDoc.exists() ? guideDoc.data().name : null;
        const organizerName = organizerDoc.exists()
          ? organizerDoc.data().name
          : null;

        // Update table data
        const tableRef = doc(firestore, 'CampAccommodationTables', tableId);
        const tableSnap = await getDoc(tableRef);

        if (!tableSnap.exists()) {
          throw new Error(
            useLanguage.getState().translations?.campAccommodation?.errors
              ?.tableNotFound || 'Accommodation table not found'
          );
        }

        const table = tableSnap.data() as CampAccommodationTable;

        // Find tent and hall
        const tentIndex = table.tents.findIndex(
          (t) => t.tentNumber === tentNumber
        );
        if (tentIndex === -1)
          throw new Error(
            useLanguage.getState().translations?.campAccommodation?.errors
              ?.tentNotFound || 'Tent not found'
          );

        const hallIndex = table.tents[tentIndex].halls.findIndex(
          (h) => h.hallNumber === hallNumber
        );
        if (hallIndex === -1)
          throw new Error(
            useLanguage.getState().translations?.campAccommodation?.errors
              ?.hallNotFound || 'Hall not found'
          );

        const hall = table.tents[tentIndex].halls[hallIndex];

        // Validate gender
        if (pilgrimData.Gender.toLowerCase() !== hall.gender) {
          throw new Error(
            useLanguage.getState().translations?.campAccommodation?.errors
              ?.genderMismatch ||
              'Cannot assign pilgrim to this hall due to gender mismatch'
          );
        }

        // Update bed data
        const updatedTents = [...table.tents];
        const updatedHalls = [...updatedTents[tentIndex].halls];
        const updatedBeds = [...updatedHalls[hallIndex].beds];

        updatedBeds[bedIndex] = {
          pilgrimId: pilgrimDoc.id,
          pilgrimName: pilgrimData.Name,
          gender: pilgrimData.Gender.toLowerCase(),
          country: pilgrimData.Residence_Country,
          guideId,
          guideName,
          packageName: pilgrimData.package_nickName,
          organizerId,
          age: calculateAge(pilgrimData.Date_of_Birth),
        };

        updatedHalls[hallIndex] = {
          ...hall,
          beds: updatedBeds,
          occupiedBeds: hall.occupiedBeds + 1,
        };

        updatedTents[tentIndex] = {
          ...table.tents[tentIndex],
          halls: updatedHalls,
          occupiedCapacity: table.tents[tentIndex].occupiedCapacity + 1,
        };

        // Update table
        await updateDoc(tableRef, {
          tents: updatedTents,
          occupiedCapacity: table.occupiedCapacity + 1,
          updatedAt: Date.now(),
        });

        set((state) => ({
          tables: {
            ...state.tables,
            [tableId]: {
              ...table,
              tents: updatedTents,
              occupiedCapacity: table.occupiedCapacity + 1,
              updatedAt: Date.now(),
            },
          },
          loading: false,
        }));
      } catch (error: any) {
        console.error('Error assigning pilgrim:', error);
        set({ error: error.message, loading: false });
        throw error;
      }
    },

    removePilgrim: async (tableId, tentNumber, hallNumber, bedIndex) => {
      set({ loading: true, error: null });
      try {
        // Check for service suspension
        const isSuspended = await useSuspensionStore
          .getState()
          .checkServiceSuspension('camp_accommodation');

        if (isSuspended) {
          throw new Error(
            useLanguage.getState().translations?.campAccommodation?.errors
              ?.serviceSuspended ||
              'Camp accommodation service is currently suspended'
          );
        }

        // Get current user info
        const currentUserId = localStorage.getItem('userId');
        const userRole = localStorage.getItem('userRole');

        if (!currentUserId || !userRole) {
          throw new Error(
            useLanguage.getState().translations?.campAccommodation?.errors
              ?.notAuthorized || 'You are not authorized to perform this action'
          );
        }

        // Check for user ban
        const isUserBanned = await useBansStore
          .getState()
          .checkUserBans(currentUserId, 'camp');

        if (isUserBanned) {
          throw new Error(
            useLanguage.getState().translations?.campAccommodation?.errors
              ?.userBanned ||
              'You are banned from using the camp accommodation service'
          );
        }

        const tableRef = doc(firestore, 'CampAccommodationTables', tableId);
        const tableSnap = await getDoc(tableRef);

        if (!tableSnap.exists()) {
          throw new Error(
            useLanguage.getState().translations?.campAccommodation?.errors
              ?.tableNotFound || 'Accommodation table not found'
          );
        }

        const table = tableSnap.data() as CampAccommodationTable;

        // Find tent and hall
        const tentIndex = table.tents.findIndex(
          (t) => t.tentNumber === tentNumber
        );
        if (tentIndex === -1)
          throw new Error(
            useLanguage.getState().translations?.campAccommodation?.errors
              ?.tentNotFound || 'Tent not found'
          );

        const hallIndex = table.tents[tentIndex].halls.findIndex(
          (h) => h.hallNumber === hallNumber
        );
        if (hallIndex === -1)
          throw new Error(
            useLanguage.getState().translations?.campAccommodation?.errors
              ?.hallNotFound || 'Hall not found'
          );

        const hall = table.tents[tentIndex].halls[hallIndex];
        const bed = hall.beds[bedIndex];

        // Check permissions
        if (userRole === 'guide' && bed.guideId !== currentUserId) {
          throw new Error(
            useLanguage.getState().translations?.campAccommodation?.errors
              ?.noPermission ||
              'You do not have permission to remove this pilgrim'
          );
        }

        if (userRole === 'organizer' && bed.organizerId !== currentUserId) {
          throw new Error(
            useLanguage.getState().translations?.campAccommodation?.errors
              ?.noPermission ||
              'You do not have permission to remove this pilgrim'
          );
        }

        // Update data
        const updatedTents = [...table.tents];
        const updatedHalls = [...updatedTents[tentIndex].halls];
        const updatedBeds = [...updatedHalls[hallIndex].beds];

        // Clear bed data
        updatedBeds[bedIndex] = {
          pilgrimId: null,
          pilgrimName: null,
          gender: null,
          country: null,
          guideId: null,
          guideName: null,
          packageName: null,
        };

        updatedHalls[hallIndex] = {
          ...hall,
          beds: updatedBeds,
          occupiedBeds: Math.max(0, hall.occupiedBeds - 1),
        };

        updatedTents[tentIndex] = {
          ...table.tents[tentIndex],
          halls: updatedHalls,
          occupiedCapacity: Math.max(
            0,
            table.tents[tentIndex].occupiedCapacity - 1
          ),
        };

        // Update table
        await updateDoc(tableRef, {
          tents: updatedTents,
          occupiedCapacity: Math.max(0, table.occupiedCapacity - 1),
          updatedAt: Date.now(),
        });

        set((state) => ({
          tables: {
            ...state.tables,
            [tableId]: {
              ...table,
              tents: updatedTents,
              occupiedCapacity: Math.max(0, table.occupiedCapacity - 1),
              updatedAt: Date.now(),
            },
          },
          loading: false,
        }));
      } catch (error: any) {
        console.error('Error removing pilgrim:', error);
        set({ error: error.message, loading: false });
        throw error;
      }
    },

    deleteTable: async (tableId) => {
      set({ loading: true, error: null });
      try {
        await deleteDoc(doc(firestore, 'CampAccommodationTables', tableId));

        set((state) => {
          const newTables = { ...state.tables };
          delete newTables[tableId];
          return { tables: newTables, loading: false };
        });
      } catch (error: any) {
        console.error('Error deleting table:', error);
        set({ error: error.message, loading: false });
        throw error;
      }
    },
  })
);

// Helper Functions
const parseExcelFile = async (file: File): Promise<CampExcelRow[]> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        const data = e.target?.result;
        const workbook = XLSX.read(data, { type: 'binary' });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet);

        const rows: CampExcelRow[] = jsonData.map((row: any) => ({
          tentNumber: parseInt(row['رقم الخيمة']),
          hallNumber: parseInt(row['رقم الصالة']),
          bedNumber: parseInt(row['عدد الاسرة']),
          gender: row['نوع الاسرة'].toLowerCase() as 'male' | 'female',
        }));

        resolve(rows);
      } catch (error) {
        reject(error);
      }
    };

    reader.onerror = (error) => reject(error);
    reader.readAsBinaryString(file);
  });
};

const processExcelData = (rows: CampExcelRow[]) => {
  const tentsMap = new Map();

  // Group rows by tent number
  rows.forEach((row) => {
    if (!tentsMap.has(row.tentNumber)) {
      tentsMap.set(row.tentNumber, []);
    }
    tentsMap.get(row.tentNumber).push(row);
  });

  const tents = [];

  // Process each tent
  for (const [tentNumber, tentRows] of tentsMap) {
    const halls = tentRows.map((row) => ({
      hallNumber: row.hallNumber,
      beds: Array(row.bedNumber)
        .fill(null)
        .map(() => ({
          pilgrimId: null,
          pilgrimName: null,
          gender: null,
          country: null,
          guideId: null,
          guideName: null,
          packageName: null,
        })),
      capacity: row.bedNumber,
      occupiedBeds: 0,
      gender: row.gender,
    }));

    const totalCapacity = halls.reduce((sum, hall) => sum + hall.capacity, 0);

    tents.push({
      tentNumber,
      halls,
      totalCapacity,
      occupiedCapacity: 0,
      gender: halls[0].gender,
    });
  }

  return tents.sort((a, b) => a.tentNumber - b.tentNumber);
};

const calculateTotalCapacity = (tents: any[]) => {
  return tents.reduce((total, tent) => total + tent.totalCapacity, 0);
};
